'use client';

import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { EntityLookup } from '@/components/lookups/EntityLookup';
import { UsersBrowser } from '@/components/users/UsersBrowser';
import { User, Building2, X, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { authenticatedApiClient } from '@/lib/api/client';
import { initiativesApi } from '@/lib/api/endpoints';
import { ASSIGNMENT_ROLE_OPTIONS } from '@sizzlenz/shared';
import type { LookupOption } from '@/components/ui/lookup';
import type { ApiEntity, ApiUser } from '@/lib/api/types';

type AssignmentType = 'user' | 'entity';

interface AddAssignmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Single initiative ID to assign to */
  initiativeId?: number;
  /** Multiple initiative IDs to assign to (bulk mode) */
  initiativeIds?: number[];
  /** Called when the user wants to edit an existing assignment instead of adding a new one */
  onSwitchToEdit?: (connection: import('@/lib/hooks/useConnections').ApiConnection) => void;
}

export function AddAssignmentDialog({
  open,
  onOpenChange,
  initiativeId,
  initiativeIds,
  onSwitchToEdit,
}: AddAssignmentDialogProps) {
  const queryClient = useQueryClient();
  const [assignmentType, setAssignmentType] = useState<AssignmentType>('user');
  const [selectedUser, setSelectedUser] = useState<ApiUser | null>(null);
  const [selectedEntity, setSelectedEntity] = useState<LookupOption<ApiEntity> | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [representing, setRepresenting] = useState<Array<{ id: number; name: string }>>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Resolve effective initiative IDs (supports both single and bulk mode)
  const effectiveInitiativeIds = useMemo(() => {
    if (initiativeIds && initiativeIds.length > 0) return initiativeIds;
    if (initiativeId) return [initiativeId];
    return [];
  }, [initiativeId, initiativeIds]);

  const isBulkMode = effectiveInitiativeIds.length > 1;
  const primaryInitiativeId = effectiveInitiativeIds[0] ?? 0;

  // Check if the selected user/entity is already assigned by looking at the cached connections
  const existingConnection = useMemo(() => {
    if (isBulkMode) return null; // Skip duplicate detection in bulk mode
    const selectedId = assignmentType === 'user' ? selectedUser?.id : selectedEntity?.data.id;
    if (!selectedId) return null;

    const allCachedQueries = queryClient.getQueriesData<any>({ queryKey: ['connections'] });
    for (const [, data] of allCachedQueries) {
      if (!data?.pages) continue;
      for (const page of data.pages) {
        const found = (page.connections ?? []).find(
          (c: any) =>
            c.fromType === assignmentType &&
            c.fromId === selectedId &&
            c.toType === 'initiative' &&
            c.toId === primaryInitiativeId
        );
        if (found) return found;
      }
    }
    return null;
  }, [
    assignmentType,
    selectedUser?.id,
    selectedEntity?.data.id,
    primaryInitiativeId,
    queryClient,
    isBulkMode,
  ]);

  const isAlreadyAssigned = !!existingConnection;

  const resetForm = () => {
    setAssignmentType('user');
    setSelectedUser(null);
    setSelectedEntity(null);
    setSelectedRoles([]);
    setNotes('');
    setRepresenting([]);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && !isSubmitting) {
      resetForm();
    }
    onOpenChange(newOpen);
  };

  const handleToggleRole = (role: string, checked: boolean) => {
    setSelectedRoles((prev) => (checked ? [...prev, role] : prev.filter((r) => r !== role)));
  };

  const handleSubmit = async () => {
    if (selectedRoles.length === 0) {
      toast.error('Select at least one role');
      return;
    }

    const fromType = assignmentType;
    const fromId = assignmentType === 'user' ? selectedUser?.id : selectedEntity?.data.id;

    if (!fromId) {
      toast.error(`Select a ${assignmentType} first`);
      return;
    }

    setIsSubmitting(true);
    try {
      if (isBulkMode) {
        // Bulk mode: call the bulk-assign endpoint for all initiative IDs
        const result = await initiativesApi.bulkAssign({
          initiativeIds: effectiveInitiativeIds,
          fromType,
          fromId,
          roles: selectedRoles,
          notes: notes.trim() || undefined,
          representing: representing.length > 0 ? representing : undefined,
        });

        const name =
          assignmentType === 'user'
            ? selectedUser?.name || selectedUser?.email
            : selectedEntity?.data.name_1;

        if (result.assignedCount > 0) {
          toast.success(
            `${name} assigned to ${result.assignedCount} initiative${result.assignedCount !== 1 ? 's' : ''}`
          );
        }
        if (result.errors && result.errors.length > 0) {
          toast.error(`Failed for ${result.errors.length} initiative(s)`);
        }

        // Invalidate connections cache to refresh assignment data
        queryClient.invalidateQueries({ queryKey: ['connections'] });
      } else {
        // Single mode: call the per-initiative endpoint
        const response: any = await authenticatedApiClient
          .post(`initiatives/${primaryInitiativeId}/connections`, {
            json: {
              toType: fromType,
              toId: fromId,
              roles: selectedRoles,
              notes: notes.trim() || undefined,
              representing: representing.length > 0 ? representing : undefined,
            },
          })
          .json();

        const name =
          assignmentType === 'user'
            ? selectedUser?.name || selectedUser?.email
            : selectedEntity?.data.name_1;

        toast.success(`${name} assigned as ${selectedRoles.join(', ')}`);

        // Build the enriched connection to put in cache.
        const enrichedConn = {
          ...(response.connection || {
            id: Date.now(),
            fromId,
            fromType,
            toId: primaryInitiativeId,
            toType: 'initiative',
            tags: [],
            systemTags: ['assignment'],
            data: {
              role: selectedRoles[0],
              relationships: selectedRoles,
              notes: notes.trim() || undefined,
              representing: representing.length > 0 ? representing : undefined,
            },
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          }),
          ...(assignmentType === 'user' && selectedUser
            ? {
                fromUser: {
                  name: selectedUser.name,
                  email: selectedUser.email,
                  image: selectedUser.image,
                },
              }
            : {}),
          ...(assignmentType === 'entity' && selectedEntity
            ? {
                fromEntity: {
                  id: selectedEntity.data.id,
                  name_1: selectedEntity.data.name_1,
                  type: selectedEntity.data.type,
                  image: selectedEntity.data.image,
                  status: selectedEntity.data.status,
                },
              }
            : {}),
        };

        queryClient.setQueriesData({ queryKey: ['connections'] }, (old: any) => {
          if (!old?.pages) return old;
          return {
            ...old,
            pages: old.pages.map((page: any, idx: number) => {
              const existingIdx = page.connections.findIndex((c: any) => c.id === enrichedConn.id);
              if (existingIdx !== -1) {
                const updated = [...page.connections];
                updated[existingIdx] = enrichedConn;
                return { ...page, connections: updated };
              }
              return idx === 0
                ? { ...page, connections: [enrichedConn, ...page.connections] }
                : page;
            }),
          };
        });
      }

      resetForm();
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to add assignment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasSelection = assignmentType === 'user' ? !!selectedUser : !!selectedEntity;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[540px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Add Assignment</DialogTitle>
          <DialogDescription>
            {isBulkMode
              ? `Assign a user or entity to ${effectiveInitiativeIds.length} selected initiatives with one or more roles.`
              : 'Assign a user or entity to this initiative with one or more roles.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2 overflow-y-auto flex-1">
          {/* Type Toggle */}
          <div className="space-y-2">
            <Label>Type</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={assignmentType === 'user' ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setAssignmentType('user');
                  setSelectedEntity(null);
                  setRepresenting([]);
                }}
                className="cursor-pointer"
              >
                <User className="h-4 w-4 mr-1.5" />
                User
              </Button>
              <Button
                type="button"
                variant={assignmentType === 'entity' ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setAssignmentType('entity');
                  setSelectedUser(null);
                  setRepresenting([]);
                }}
                className="cursor-pointer"
              >
                <Building2 className="h-4 w-4 mr-1.5" />
                Entity
              </Button>
            </div>
          </div>

          {/* Entity picker */}
          {assignmentType === 'user' ? (
            <div className="space-y-2">
              <Label>User</Label>
              {selectedUser ? (
                <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  {selectedUser.image ? (
                    <img
                      src={selectedUser.image}
                      alt={selectedUser.name || 'User'}
                      className="h-8 w-8 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                      <User className="h-4 w-4 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {selectedUser.name || 'Unnamed User'}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {selectedUser.email}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedUser(null)}
                    className="cursor-pointer flex-shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden h-56 flex flex-col">
                  <UsersBrowser
                    onSelectImmediate={setSelectedUser}
                    mode="single"
                    showSelectedCount={false}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Entity</Label>
              <EntityLookup
                value={selectedEntity}
                onChange={setSelectedEntity}
                placeholder="Search for an entity..."
              />
            </div>
          )}

          {/* Already assigned warning */}
          {isAlreadyAssigned && (
            <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p>This {assignmentType} is already assigned to this initiative.</p>
                {onSwitchToEdit && existingConnection && (
                  <button
                    type="button"
                    onClick={() => {
                      onOpenChange(false);
                      onSwitchToEdit(existingConnection);
                    }}
                    className="mt-1 text-amber-900 font-medium underline underline-offset-2 hover:text-amber-950 cursor-pointer"
                  >
                    Edit existing assignment →
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Role Checkboxes */}
          <div className="space-y-2">
            <Label>Roles (select at least one)</Label>
            <div className="space-y-1">
              {ASSIGNMENT_ROLE_OPTIONS.map((role) => (
                <label
                  key={role.value}
                  className="flex items-center gap-3 p-2 rounded-md hover:bg-gray-50 cursor-pointer"
                >
                  <Checkbox
                    checked={selectedRoles.includes(role.value)}
                    onCheckedChange={(checked) => handleToggleRole(role.value, !!checked)}
                  />
                  <span className="text-sm font-medium">{role.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Representing entities */}
          <div className="space-y-2">
            <Label>Representing (optional)</Label>
            <EntityLookup
              value={null}
              onChange={(option) => {
                if (!option) return;
                if (representing.some((e) => e.id === option.data.id)) return;
                setRepresenting((prev) => [
                  ...prev,
                  { id: option.data.id, name: option.data.name_1 || `Entity ${option.data.id}` },
                ]);
              }}
              placeholder="Add an entity they represent..."
              excludeIds={representing.map((e) => e.id)}
            />
            {representing.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-1">
                {representing.map((entity) => (
                  <span
                    key={entity.id}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700"
                  >
                    <Building2 className="w-3 h-3" />
                    {entity.name}
                    <button
                      type="button"
                      onClick={() =>
                        setRepresenting((prev) => prev.filter((e) => e.id !== entity.id))
                      }
                      className="hover:text-indigo-900 cursor-pointer"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about this assignment..."
              rows={2}
            />
          </div>
        </div>

        <DialogFooter className="pt-2">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isSubmitting}
            className="cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              !hasSelection || selectedRoles.length === 0 || isSubmitting || isAlreadyAssigned
            }
            className="cursor-pointer"
          >
            {isSubmitting ? 'Adding...' : 'Add Assignment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
